import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Leaf } from "lucide-react";
import { SITE } from "@/data/content";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/services", label: "My Services" },
  { to: "/contact", label: "Contact Us" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#FAF8F4]/90 backdrop-blur-xl border-b border-[#6B8F71]/15 shadow-[0_4px_20px_rgba(107,143,113,0.06)]"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-2.5 group"
        >
          <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#6B8F71]/12 text-[#355C4A] group-hover:bg-[#6B8F71]/20 transition-colors">
            <Leaf strokeWidth={1.5} className="w-5 h-5" />
          </span>
          <span className="leading-none">
            <span className="block font-serif text-2xl text-[#355C4A]">
              {SITE.name}
            </span>
            <span className="block text-[10px] tracking-[0.22em] uppercase text-[#6B8F71]">
              Counselling
            </span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[15px] tracking-wide transition-colors relative py-1 ${
                  isActive
                    ? "text-[#355C4A]"
                    : "text-[#5A655F] hover:text-[#355C4A]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  <span
                    className={`absolute left-0 -bottom-0.5 h-px bg-[#C9A96A] transition-all duration-300 ${
                      isActive ? "w-full" : "w-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-book-btn"
            className="bg-[#6B8F71] text-white px-6 py-2.5 rounded-full hover:bg-[#355C4A] hover:-translate-y-0.5 transition-all duration-300 text-sm font-medium"
          >
            Book a Session
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-[#355C4A] p-2"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-[#FAF8F4] border-t border-[#6B8F71]/15 px-6 py-6 flex flex-col gap-4"
        >
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-lg ${isActive ? "text-[#355C4A]" : "text-[#5A655F]"}`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="mobile-book-btn"
            className="mt-2 bg-[#6B8F71] text-white text-center px-6 py-3 rounded-full text-sm font-medium"
          >
            Book a Session
          </Link>
        </div>
      )}
    </header>
  );
};

export default Navbar;
